import { browserBundleService } from '@flowio/browser-bundle';
import AcceptedPaymentMethods from '@flowio/accepted-payment-methods';
import { parseQueryString } from '@flowio/web-sdk';
import constants from './constants';
import { initialize as blazeInitialize } from './blaze';
import initFraud from './initFraud';
import { listenForForterToken, loadForter } from '../../../app/utilities/get-forter';

const assign = require('lodash/assign');
const get = require('lodash/get');
const set = require('lodash/set');
const isNil = require('lodash/isNil');
const optin = require('@flowio/optin');
const session = require('@flowio/session');
const storage = require('@flowio/storage');
const initSession = require('@flowio/session/lib/init');
const { init: pixelInit, getPixel } = require('./utilities/pixel');
const SchemaOrgGenerator = require('./schema-org').default;
const variants = require('./variants').default;
const config = require('./config');
const experience = require('./experience');
const events = require('./events');
const { attachListeners } = require('./listeners');
const cache = require('./cache');
const log = require('./utilities/log');
const optionsError = require('./utilities/options-error');
const optionsSuccess = require('./utilities/options-success');
const syncCartAttributes = require('./utilities/sync-cart-attributes');
const setConsumerId = require('./set-consumer-id');
const { displayShippingWindow } = require('./shipping-window');
const { getCart, init: cartInit } = require('./cart_unified/cart-unified');
const internalLocalize = require('./localize');
const { isApiShopifyLocalizationMethod } = require('./utilities/shopify-localization-method');

function maybeResetSessionByParams() {
  return new Promise(resolve =>
    experience.resetSessionByUrlParams({
      success: resolve,
      error: function errorSetExperience(e) {
        events.trigger('error', e);
        resolve(); // initialize the page anyway
      },
    }));
}

const setInitialized = () => {
  window.Flow.initialized = true;
};

const buildCache = () => {
  window.Flow.cache = cache.buildCache();
};

const syncVariants = () => new Promise((resolve) => {
  if (session.getExperience()) {
    variants.syncVariants({
      forceReload: isApiShopifyLocalizationMethod(),
      success: () => {
        resolve();
      },
      error: (error) => {
        events.trigger('error', error);
        resolve();
      },
    });
  } else {
    resolve();
  }
});


const localize = () => internalLocalize({ buildCache: false });

const initConsent = () => (
    new Promise((resolve) => {
      optin.getAndDisplayOptin(
        {
          targetElement: get(window, 'Flow.optinTarget'),
          shouldSetTimeout: true,
        },
      );
      resolve();
    })
);

const addDocumentLocalizedClass = () => {
  if (experience.getExperience()) {
    document.documentElement.className += ' flow-localized';
  }
};

const triggerReadyEvent = () => {
  events.trigger(events.constants.READY);
};

const triggerPageViewEvent = () => {
  events.triggerPageViewEvent();
};

const initializeUnifiedCart = (options, sess) => {
  const settings = {
    flowCartId: get(sess, 'attributes.cart_id'),
    flowApiBaseURI: 'https://api.flow.io',
    ...options,
  };
  set(window.Flow, 'shopify.cart', cartInit(settings));
  return Promise.resolve();
};

const initializePixel = () => {
  const pixelConfig = storage.memory.get('pixelConfig');

  if (pixelConfig != null) {
    pixelInit(pixelConfig);
    set(window.Flow, 'pixel', getPixel());
  }
};

function createSchema() {
  const schemaOrg = new SchemaOrgGenerator();
  // eslint-disable-next-line no-console
  schemaOrg.generateSchemaOrgJson().catch(e => console.log(e));
  return schemaOrg;
}

function renderPaymentMethodIcons() {
  const acceptedPaymentMethods = new AcceptedPaymentMethods();
  const mountNodes = [].slice.call(
    document.getElementsByClassName(constants.CLASS_FLOW_ACCEPTED_PAYMENT_METHODS),
  );
  events.on(
    events.constants.BROWSER_BUNDLE_DATA,
    (browserBundle) => {
      acceptedPaymentMethods.render({
        paymentMethods: get(browserBundle, 'payment_methods.displayed', []),
        mountNodes,
      });
    },
  );

  return Promise.resolve();
}

const fetchBrowserBundle = options => browserBundleService.fetch(
  { session: (options && options.session) || session.getSession() },
)
  .then((browserBundle) => {
    events.trigger(events.constants.BROWSER_BUNDLE_DATA, browserBundle);
    return browserBundle;
  }).catch((e) => {
    log('[flow.js] bundle fetch error', e);
    events.trigger(events.constants.BROWSER_BUNDLE_FAILURE, e);
  });

/**
 * Perform automatic tasks.
 */
function initialize(options, sess) {
  return Promise.resolve()
    .then(attachListeners)
    .then(() => fetchBrowserBundle(options))
    .then(() => initializeUnifiedCart(options, sess))
    .then(maybeResetSessionByParams)
    .then(setInitialized)
    .then(() => loadForter(sess))
    .then(buildCache)
    .then(syncVariants)
    .then(localize)
    .then(initFraud)
    .then(initConsent)
    .then(addDocumentLocalizedClass)
    .then(setConsumerId)
    .then(listenForForterToken)
    .then(initializePixel)
    .then(blazeInitialize)
    .then(triggerReadyEvent)
    .then(triggerPageViewEvent)
    .then(displayShippingWindow)
    .then(createSchema)
    .then(renderPaymentMethodIcons)
    .catch((error) => {
      events.trigger(events.constants.ERROR, error);
      log('[flow.js] init error', error);
    });
}

/**
 * Make sure cookies and session look good. If not, set it up.
 */
function setup(options) {
  const params = parseQueryString(window.location.search);
  const useSessionStorage = get(params, 'flow_session_storage') === 'temporary';
  const opts = assign(
    {
      integration: 'shopify',
      shop: config.SHOPIFY_SHOP,
      forceNewSession: !isNil(params.flow_create_new_session),
      sessionId: params.flow_session_id,
      useSessionStorage,
    },
    options,
  );
  const o = (params.flow_disable) ? assign(opts, { experience: null }) : opts;
  initSession(o);
}

function init(options = {}) {
  if (window.Flow.manualInit && !options.force) {
    optionsError(options, 'Failed to initialize Flow. When Flow.manualInit is enabled, provide the force option to init. Example: Flow.init({ force: true })');
    return;
  }

  setup(assign({}, options, {
    onSuccess: (sess) => {
      initialize(options, sess).then(() => {
        optionsSuccess(options, sess);
      });
    },
    onUpserted: (sess) => {
      syncCartAttributes(sess, getCart(sess));
      if (options.onUpserted) {
        options.onUpserted(sess);
      }
    },
  }));
}

module.exports = init;



// WEBPACK FOOTER //
// ./static/js/v0/init.js